import type { CSSProperties } from "react";
import TaskTimingActions from "@/components/tasks/timing/TaskTimingActions/TaskTimingActions";
import TaskTimingHeader from "@/components/tasks/timing/TaskTimingHeader/TaskTimingHeader";
import type { TaskTimingColors } from "@/components/tasks/timing/TaskTimingFields/TaskTimingFields";
import PatternTimingFields, {
  derivedPatternEndTime,
  patternNowValue,
  type PatternTimingValue,
} from "./PatternTimingFields";

export type PatternScheduleFormValue = PatternTimingValue & {
  alertEnabled: boolean;
};
export type PatternScheduleFormProps = {
  kind?: "pattern";
  value: PatternScheduleFormValue;
  onChange: (
    value: PatternScheduleFormValue,
    reason: "start-time" | "duration" | "alert" | "now" | "clear"
  ) => void;
  onCommit?: (value: PatternScheduleFormValue, reason: "duration") => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alwaysOpen?: boolean;
  colors: TaskTimingColors;
  emptyLabel?: string;
};

export default function PatternScheduleForm({
  kind = "pattern",
  value,
  onChange,
  onCommit,
  open,
  onOpenChange,
  alwaysOpen = false,
  colors,
  emptyLabel = "Default schedule",
}: PatternScheduleFormProps) {
  const timing = {
    startDate: "",
    endDate: "",
    startTime: value.startTime,
    endTime: derivedPatternEndTime(value.startTime, value.durationMinutes),
    durationMinutes: value.durationMinutes,
  };
  const style = {
    "--section-bar": colors.bar,
    "--section-soft": colors.soft,
    "--section-ink": colors.ink,
  } as CSSProperties;
  return (
    <section
      className="task-timing-section pattern-schedule-form"
      style={style}
    >
      <TaskTimingHeader
        kind={kind}
        timing={timing}
        emptyLabel={emptyLabel}
        alertEnabled={value.alertEnabled}
        open={open || alwaysOpen}
        expandable={!alwaysOpen}
        onClick={() => {
          if (!alwaysOpen) onOpenChange(!open);
        }}
        color={colors.ink}
      />
      <div
        className={`timing-body ${open || alwaysOpen ? "is-open" : "is-closed"}`}
      >
        <PatternTimingFields
          value={{
            startTime: value.startTime,
            durationMinutes: value.durationMinutes,
          }}
          onChange={(next, reason) => onChange({ ...value, ...next }, reason)}
          onDurationBlur={() => onCommit?.(value, "duration")}
          alertEnabled={value.alertEnabled}
          onAlertEnabledChange={alertEnabled =>
            onChange({ ...value, alertEnabled }, "alert")
          }
          colors={colors}
        />
      </div>
      <TaskTimingActions
        onNow={() => onChange({ ...value, ...patternNowValue(value) }, "now")}
        onClear={() =>
          onChange({ ...value, startTime: "", alertEnabled: false }, "clear")
        }
        color={colors.ink}
      />
    </section>
  );
}
